import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Link, Tab, Typography } from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import {
    FieldSearchContainerValue,
    FieldSearchRecordSeriesValue,
    FieldSearchSetNameValue,
    FieldValidationAltCode,
    FieldValidationBarcode,
    useGetItemActivityQuery,
    useGetItemByIdQuery,
    useGetItemsAdvancedSearchQuery,
    useGetItemsBasicSearchQuery,
    useGetUserInfoQuery,
    useUpdateItemMutation
} from "../../services/grace";
import { InventoryEditItem } from "../../model/Inventory";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import { useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";
import { FormikBag, withFormik } from "formik";
import * as Yup from "yup";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { format } from "date-fns";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ActivityGrid from "../../components/grids/ActivityGrid/ActivityGrid";
import { DynamicFormProps } from "./AddDynamicItemForm";
import DynamicBaseItemForm, {DynamicBaseItemFormProps} from "./DynamicBaseItemForm";
import { APIFormFieldDef } from "../../model/User";

enum ItemFormTab {
    details = 'details',
    activity = 'activity'
}

interface ItemParams {
    id: string;
}

export interface FormData extends InventoryEditItem {
    setNameValue: FieldSearchSetNameValue | null;
    recordSeriesValue: FieldSearchRecordSeriesValue | null;
    containerValue: FieldSearchContainerValue | null;
}

enum FieldTypes {
    Barcode = 'barcode',
    AltCode = 'altcode',
}

declare type ValidationFieldType = FieldTypes.Barcode | FieldTypes.AltCode;

interface SaveClick {
    type: string,
    timestamp: string,
}

export default function EditDynamicItemForm(props: DynamicFormProps): JSX.Element {
    const { id } = useParams<ItemParams>();
    const { formDefinition, formType } = props;
    const history = useHistory();

    const [updateItem] = useUpdateItemMutation();
    const alertDispatch = useAlertDispatch();

    const { data: userData } = useGetUserInfoQuery();

    const disabled = userData !== undefined && !userData.permissions.editItem;

    const [itemFormTab, setItemFormTab] = useState<ItemFormTab>(ItemFormTab.details);
    const [editFormValues, setEditFormValues] = useState<FormData|null>(null);
    const [inProgress, setInProgress] = useState(false);

    const [saveClicked, setSaveClicked] = useState<SaveClick>({type: "", timestamp: ""});
    useEffect(() => {
        if (saveClicked.type !== "") {
            submitButtonRef.current?.click();
        }
    }, [saveClicked])

    const [formData, setFormData] = useState<FormData | null>(null);
    useEffect(() => {
        if (formData !== null) {
            setInProgress(true);
            const {containerValue, recordSeriesValue, setNameValue, ...rest} = formData;

            // Note - do not reset "inProgress" after a successful add/update as doing
            // so will cause the data to refresh (due to cache invalidation) which
            // in turn causes a rebuild of the entire form as we navigate back to
            // the inventory page. This causes a memory leak so should be avoided.
            updateItem({
                ...rest,
                containerId: containerValue?.id ?? "",
                containerBarcode: containerValue?.barcode ?? "",
                containerAltCode: containerValue?.altCode ?? "",
                recordSeries: recordSeriesValue?.recordSeriesId ?? "",
                recordSeriesId: recordSeriesValue?.recordSeriesId ?? "",
                recordSeriesCode: recordSeriesValue?.recordSeriesCode ?? "",
                recordSeriesTitle: recordSeriesValue?.recordSeriesTitle ?? "",
                setName: setNameValue?.setName ?? "",
            }).unwrap().then((result) => {
                history.replace("/inventory");
            }).catch((error) => {
                console.log(error);
                const err = error as FetchBaseQueryError;

                if (err.status === 401) {
                    alertDispatch({
                        type: 'add',
                        payload: {
                            type: "auth",
                            message: "Your session has expired",
                        }
                    });
                } else {
                    alertDispatch({
                        type: 'add',
                        payload: {
                            type: "general",
                            message: "Error saving item - please try again",
                        }
                    });
                }

                setInProgress(false);
            });
        }
    }, [formData]);

    const getRequestBody = (validationType: ValidationFieldType, id: string, type: string, data: FieldValidationBarcode | FieldValidationAltCode):string => {
        if (validationType === FieldTypes.Barcode) {
            return JSON.stringify({
                context: (id === undefined || id === "") ? type : id,
                field: 'barcode',
                value: data,
            });
        } else {
            if (id !== "") {
                return JSON.stringify({
                    context: id,
                    field: 'altCode',
                    value: data,
                });
            } else {
                return JSON.stringify({
                    field: 'altCode',
                    value: data,
                });
            }
        }
    }

    const testField = (field: APIFormFieldDef, value: string, name: string): boolean => {
        if (field && field.regex !== undefined && field.regex !== "") {
            try {
                const regex = new RegExp(field.regex)
                return regex.test(value);
            } catch {
                console.warn(`unable to test value of ${name} using regex: ${field.regex}`);
            }
        }

        // Tests pass by default, so we can deal with broken regexes
        return true;
    }

    const validationSchema = Yup.object().shape({
        destroyDate: Yup.date().nullable(),
        barcode: Yup.string()
            .required('you must specify a barcode')
            .test({
                name: "valid-barcode",
                test: async (value, { parent, createError }) => {
                    if (value === undefined || value.length === 0) {
                        return false; // A barcode must be specified
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "barcode")

                    if (field !== undefined && !testField(field, value, "barcode")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: getRequestBody(
                                FieldTypes.Barcode,
                                parent.id,
                                parent.type,
                                {
                                    barcode: value,
                                }),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        altCode: Yup.string()
            .test({
                name: "valid-altCode",
                test: async (value, { parent, createError }) => {
                    const field = formDefinition.fields.find(f => f.fieldId === "altCode")

                    if (field !== undefined) {
                        if (field.regex !== undefined && field.regex !== "") {
                            if (value === undefined || value.length === 0) {
                                return false; // "Alt. code" won't match the regex so this should fail
                            } else  if (!testField(field, value, "altCode")) {
                                return createError({
                                    message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                                });
                            }
                        }
                    }

                    if (value === undefined || value.length === 0) {
                        return true; // "Alt. code" is not required at this point
                    }

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: getRequestBody(
                                FieldTypes.AltCode,
                                parent.id,
                                parent.type,
                                {
                                    altCode: value,
                                    accountLevel1: parent.accountLevel1,
                                    accountLevel2: parent.accountLevel2,
                                    accountLevel3: parent.accountLevel3,
                                }),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        contentsRangeStart: Yup.string()
            .test({
                name: "valid-contentsRange",
                test: async (value, { parent, createError }) => {
                    if (value === undefined && parent.contentsRangeEnd === undefined) {
                        return true;
                    }

                    if ((value === undefined && parent.contentsRangeEnd !== undefined) || (value !== undefined && parent.contentsRangeEnd === undefined)) {
                        return createError({
                            message: "you must enter a start and end, not one or the other",
                        });
                    }

                    const body = {
                        field: 'contentsRangeStart',
                        value: {
                            contentsRangeStart: value,
                            contentsRangeEnd: parent.contentsRangeEnd,
                        }
                    };

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: JSON.stringify(body),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        contentsRangeEnd: Yup.string(),
        contentsStartDate: Yup.date()
            .test({
                name: "valid-contentsDateRange",
                test: (value, { parent, createError }) => {
                    // Catch if value > parent, value undefined or parent undefined but not both
                    if (value === undefined && parent.contentsEndDate === undefined) {
                        return true; // It's ok for both to be empty
                    }

                    if ((value === undefined && parent.contentsEndDate !== undefined) || (value !== undefined && parent.contentsEndDate === undefined)) {
                        return createError({
                            message: "you must enter a start and end date, not one or the other",
                        });
                    }

                    if (value !== undefined && value > parent.contentsEndDate) {
                        return createError({
                            message: "start date must be less than or equal to end date",
                        });
                    }

                    return true;
                }
            }),
        contentsEndDate: Yup.date(),
        shortDescription: Yup.string()
            .test({
                name: "valid-shortDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "shortDescription")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "shortDescription")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        longDescription: Yup.string()
            .test({
                name: "valid-longDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "longDescription")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "longDescription")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        contents: Yup.string()
            .test({
                name: "valid-shortDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "contents")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "contents")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customDate: Yup.date().nullable(),
        customField1: Yup.string()
            .test({
                name: "valid-customField1",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField1")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField1")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField2: Yup.string()
            .test({
                name: "valid-customField2",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField2")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField2")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField3: Yup.string()
            .test({
                name: "valid-customField3",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField3")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField3")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField4: Yup.string()
            .test({
                name: "valid-customField4",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField4")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField4")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
    });

    const handleSubmit = (values: FormData, formikBag: FormikBag<DynamicBaseItemFormProps, FormData>) => {
        const { setSubmitting } = formikBag;
        setFormData(values);
        setSubmitting(false);
    };

    const {
        data: itemData,
        error: itemError,
        isFetching: itemIsFetching,
        isError: itemIsError,
    } = useGetItemByIdQuery(id, {skip: id === undefined || inProgress});

    useEffect(() => {
        if (!itemIsFetching && itemData !== undefined) {
            const formData = {
                ...itemData,
                setNameValue: null,
                recordSeriesValue: null,
                containerValue: null,
            }
            setEditFormValues(formData);
        } else if (itemIsError) {
            const err = itemError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        open: true,
                        type: "general",
                        message: "Error retrieving item data - please try again",
                    }
                });
            }
        }
    }, [itemData, itemIsFetching, itemIsError, itemError, alertDispatch]);

    const {
        data: activityData,
        error: activityError,
        isFetching: activityIsFetching,
        isError: activityIsError,
    } = useGetItemActivityQuery(id, {skip: id === undefined});

    const handleClose = () => {
        history.replace("/inventory");
    }

    const handleItemFormTabChange = (tab: ItemFormTab) => {
        setItemFormTab(tab);
    };

    // Keep a link to the inventory data so that it stays cached. Otherwise, the inventory grid will
    // reload if we return to that page after the data has expired (by default this is 60 seconds)
    // -- START CACHING --
    const basicSearch = useAppSelector((state: RootState) => state.inventory.basicSearch);
    const advancedSearch = useAppSelector((state: RootState) => state.inventory.advancedSearch);
    useGetItemsBasicSearchQuery(basicSearch.params, { skip: !basicSearch.active });
    useGetItemsAdvancedSearchQuery(advancedSearch.params, { skip: !advancedSearch.active });
    // -- END CACHING --

    const submitButtonRef= useRef<HTMLButtonElement>(null);

    const FormikDynamicEditItemForm = useMemo(() => withFormik<DynamicBaseItemFormProps, FormData>({

        validateOnBlur: false,
        validateOnChange: false,

        validationSchema: validationSchema,

        mapPropsToValues: (props) => {
            const values = {...props.formValues}

            if (props.formValues.id === "") {
                const currentDate = format(new Date(),"yyyy-MM-dd");
                values.status = "pending";
                values.statusDate = currentDate;
                values.addDate = currentDate;
            }

            values.permanent = Boolean(props.formValues.permanent);

            if (props.formValues.containerId !== "") {
                values.containerValue = {
                    id: props.formValues.containerId,
                    barcode: props.formValues.containerBarcode,
                    altCode: props.formValues.containerAltCode,
                    accountLevel1: props.formValues.accountLevel1,
                    accountLevel2: props.formValues.accountLevel2,
                    accountLevel3: props.formValues.accountLevel3,
                }
            } else {
                values.containerValue = null;
            }

            if (props.formValues.setName !== "") {
                values.setNameValue = {
                    setName: props.formValues.setName,
                }
            } else {
                values.setNameValue = null;
            }

            if (props.formValues.recordSeriesId !== "") {
                values.recordSeriesValue = {
                    recordSeriesId: props.formValues.recordSeriesId,
                    recordSeriesCode: props.formValues.recordSeriesCode,
                    recordSeriesTitle: props.formValues.recordSeriesTitle,
                    legalHold: props.formValues.legalHold,
                }
            } else {
                values.recordSeriesValue = null;
            }

            return values;
        },

        handleSubmit: handleSubmit,
    })(DynamicBaseItemForm), []);

    const itemTypeLabel = itemData?.type === "container" ? "carton" : itemData?.type;

    if (!itemIsFetching && itemIsError) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100vh"
            >
                <Typography>
                    An error occurred retrieving data - click
                    <Link onClick={() => history.replace("/inventory")}>here</Link>
                    to return to the inventory page
                </Typography>
            </Box>
        );
    } else if (!itemIsFetching && !itemIsError && editFormValues !== null) {
        return (
            <Box>
                <TabContext value={itemFormTab}>
                    <GDGAppBar title={`Edit ${itemTypeLabel} ${itemData?.barcode}`} drawerWidth={0} onClose={handleClose}
                        tabs={
                            <TabList
                               onChange={(_, newTab) => handleItemFormTabChange(newTab)}
                               TabIndicatorProps={{
                                   style: {
                                       background: "#db291d",
                                   }
                               }}
                            >
                               <Tab label="Details" value={ItemFormTab.details} sx={{width: "160px"}}/>
                               <Tab label="Activity" value={ItemFormTab.activity} sx={{width: "160px"}}/>
                            </TabList>
                        }
                    >
                        {
                            inProgress &&
                            <CircularProgress color="secondary" size={20}/>
                        }

                        {
                            itemFormTab !== ItemFormTab.activity &&
                            <GDGAppBarButton
                                disabled={disabled || inProgress}
                                onClick={() => submitButtonRef?.current?.click()}
                                text="Save"
                                icon={["fas", "save"]}
                            />
                        }
                    </GDGAppBar>
                    <TabPanel value={ItemFormTab.details} sx={{marginTop: 5}}>
                        <FormikDynamicEditItemForm
                            type={formType}
                            disabled={inProgress}
                            submitButtonRef={submitButtonRef}
                            formDefinition={formDefinition}
                            formValues={editFormValues}
                        />
                    </TabPanel>
                    <TabPanel value={ItemFormTab.activity} sx={{marginTop: 5}}>
                        <Box sx={{
                            height: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '96vw',
                        }}>
                            <ActivityGrid
                                rows={activityData === undefined ? [] : activityData}
                            />
                        </Box>
                    </TabPanel>
                </TabContext>
            </Box>
        );
    } else {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100vh"
            >
                <CircularProgress/>
            </Box>
        );
    }
}
